import { validateAnswers, type ExecutableDiscoveryModel } from "@/domain/discovery-model";
import { resolveDiscoveryPath } from "@/domain/discovery-navigation";
import type { Discovery } from "@/domain/types";

export type JourneyProgress = Pick<
  Discovery,
  "answers" | "currentQuestionId" | "currentStepId" | "questionIndex" | "presentedQuestions"
>;

/** Reconcile a progress update/resume without ever changing the model or its binding. */
export function reconcileJourneyProgress<T extends JourneyProgress>(
  model: ExecutableDiscoveryModel,
  session: T,
): T {
  validateAnswers(model, session.answers);
  const route = resolveDiscoveryPath(model, session.answers);
  const cursor = route.cursor(session.currentQuestionId, session.questionIndex);
  return {
    ...session,
    answers: route.answers,
    currentQuestionId: cursor.question.id,
    currentStepId: cursor.stepId,
    // Retain the historical storage index as derived metadata, never as traversal state.
    questionIndex: cursor.questionIndex,
    presentedQuestions:
      session.presentedQuestions?.filter((p) => route.questionIds.has(p.questionId)) ?? [],
  };
}

/** Completed historical results remain immutable. Only unfinished sessions are repaired. */
export function resumeJourney(model: ExecutableDiscoveryModel, session: Discovery): Discovery {
  return session.status === "in_progress" ? reconcileJourneyProgress(model, session) : session;
}
